import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Snackbar,
  Card,
  CardContent,
  CardHeader
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(getApiUrl('/api/users'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('خطا در دریافت لیست کاربران', 'error');
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({ username: '', password: '' });
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setPasswordData({ newPassword: '', confirmPassword: '' });
    setSelectedUser(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(getApiUrl('/api/users'), formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSnackbar('کاربر با موفقیت اضافه شد', 'success');
      handleClose();
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      showSnackbar(error.response?.data?.error || 'خطا در ایجاد کاربر', 'error');
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('آیا از حذف این کاربر اطمینان دارید؟')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(getApiUrl(`/api/users/${userId}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        showSnackbar('کاربر با موفقیت حذف شد', 'success');
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        showSnackbar(error.response?.data?.error || 'خطا در حذف کاربر', 'error');
      }
    }
  };

  const handleChangePassword = (user) => {
    setSelectedUser(user);
    setPasswordDialogOpen(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showSnackbar('رمز عبور جدید و تایید آن مطابقت ندارند', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(getApiUrl(`/api/users/${selectedUser.id}/password`), {
        newPassword: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSnackbar('رمز عبور با موفقیت تغییر کرد', 'success');
      handlePasswordDialogClose();
    } catch (error) {
      console.error('Error changing password:', error);
      showSnackbar(error.response?.data?.error || 'خطا در تغییر رمز عبور', 'error');
    }
  };

  return (
    <Box sx={{
      p: { xs: 2, md: 4 },
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
    }}>
      {/* هدر صفحه */}
      <Box sx={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: 4,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        p: 4,
        mb: 3,
        border: '1px solid rgba(0, 77, 52, 0.1)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '100px',
          height: '100px',
          background: 'linear-gradient(135deg, rgba(0, 77, 52, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
          borderRadius: '50%',
          transform: 'translate(30px, -30px)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '80px',
          height: '80px',
          background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(251, 191, 36, 0.1) 100%)',
          borderRadius: '50%',
          transform: 'translate(-20px, 20px)',
        }
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <Box>
            <Typography variant="h4" sx={{
              fontFamily: 'Yekan, Vazir',
              mb: 1,
              background: 'linear-gradient(135deg, #004d34 0%, #004d34 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold',
            }}>
              مدیریت کاربران
            </Typography>
            <Typography sx={{
              fontFamily: 'Yekan, Vazir',
              color: '#64748b',
              fontSize: '1rem',
            }}>
              افزودن، ویرایش و حذف کاربران سیستم
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
            sx={{
              background: 'linear-gradient(135deg, #004d34 0%, #004d34 100%)',
              borderRadius: '25px',
              px: 3,
              py: 1.5,
              fontFamily: 'Yekan, Vazir',
              fontWeight: 'bold',
              boxShadow: '0 8px 25px rgba(0, 77, 52, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #004d34 0%, #004d34 100%)',
                boxShadow: '0 12px 35px rgba(0, 77, 52, 0.4)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            افزودن کاربر
          </Button>
        </Box>
      </Box>

      {/* جدول کاربران */}
      <Card sx={{
        borderRadius: '18px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        overflow: 'hidden',
      }}>
        <CardHeader
          title={
            <Typography variant="h6" sx={{
              fontFamily: 'Yekan, Vazir',
              fontWeight: 'bold',
              color: '#004d34',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>
              <PersonIcon />
              لیست کاربران ({users.length})
            </Typography>
          }
          sx={{
            background: 'linear-gradient(135deg, #F2F6FA 0%, #f8fafc 100%)',
            borderBottom: '1px solid #e5eaef',
          }}
        />
        <CardContent sx={{ p: 0 }}>
          <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontFamily: 'Yekan, Vazir', fontWeight: 'bold' }}>
                    نام کاربری
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Yekan, Vazir', fontWeight: 'bold' }}>
                    شناسه کاربر
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Yekan, Vazir', fontWeight: 'bold', textAlign: 'center' }}>
                    عملیات
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell sx={{ fontFamily: 'Yekan, Vazir' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ color: '#004d34', fontSize: '1.2rem' }} />
                        {user.username}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Yekan, Vazir' }}>
                      ID: {user.id}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton
                          onClick={() => handleChangePassword(user)}
                          sx={{
                            color: '#46caeb',
                            '&:hover': {
                              backgroundColor: 'rgba(70, 202, 235, 0.1)',
                            },
                          }}
                          title="تغییر رمز عبور"
                        >
                          <LockIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(user.id)}
                          sx={{
                            color: '#fb977d',
                            '&:hover': {
                              backgroundColor: 'rgba(251, 151, 125, 0.1)',
                            },
                          }}
                          title="حذف کاربر"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* دیالوگ افزودن کاربر */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Yekan, Vazir', fontWeight: 'bold', color: '#004d34' }}>
          افزودن کاربر جدید
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="نام کاربری"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              required
              sx={{ mb: 2, fontFamily: 'Yekan, Vazir' }}
            />
            <TextField
              fullWidth
              label="رمز عبور"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              helperText="رمز عبور باید حداقل ۶ کاراکتر باشد"
              sx={{ fontFamily: 'Yekan, Vazir' }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleClose} sx={{ fontFamily: 'Yekan, Vazir' }}>
              انصراف
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #004d34 0%, #004d34 100%)',
                fontFamily: 'Yekan, Vazir',
                fontWeight: 'bold',
              }}
            >
              افزودن کاربر
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* دیالوگ تغییر رمز عبور */}
      <Dialog open={passwordDialogOpen} onClose={handlePasswordDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Yekan, Vazir', fontWeight: 'bold', color: '#004d34' }}>
          تغییر رمز عبور - {selectedUser?.username}
        </DialogTitle>
        <form onSubmit={handlePasswordSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="رمز عبور جدید"
              name="newPassword"
              type="password"
              value={passwordData.newPassword}
              onChange={handlePasswordInputChange}
              required
              helperText="رمز عبور جدید باید حداقل ۶ کاراکتر باشد"
              sx={{ mb: 2, fontFamily: 'Yekan, Vazir' }}
            />
            <TextField
              fullWidth
              label="تایید رمز عبور جدید"
              name="confirmPassword"
              type="password"
              value={passwordData.confirmPassword}
              onChange={handlePasswordInputChange}
              required
              sx={{ fontFamily: 'Yekan, Vazir' }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handlePasswordDialogClose} sx={{ fontFamily: 'Yekan, Vazir' }}>
              انصراف
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #46caeb 0%, #46caeb 100%)',
                fontFamily: 'Yekan, Vazir',
                fontWeight: 'bold',
              }}
            >
              تغییر رمز عبور
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar برای نمایش پیام‌ها */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ fontFamily: 'Yekan, Vazir' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
